import React, { useEffect, useState } from 'react';
import { MDTypography, MDBox } from '@components';
import { Grid, MenuItem, Select, Switch, Tooltip } from '@mui/material';
import { sendAmplitudeData } from '@utils/amplitude';
import { SELECT_SORT_ORDER } from '@constants/eventNames';

interface Props {
  isDisabled: boolean;
  setSortOrder: (sortOrder: string) => void;
  sortOrder: string;
}

const SortOrder: React.FC<Props> = ({
  isDisabled,
  setSortOrder,
  sortOrder
}) => {
  const [sortOrderToolTip, setSortOrderToolTip] = useState(
    'Click to select sort order'
  );
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [hasUserSort, setHasUserSort] = useState(false);

  const hasSort = ['ascending', 'descending'].includes(sortOrder as string);

  useEffect(() => {
    setHasUserSort(['ascending', 'descending'].includes(sortOrder as string));
  }, [sortOrder]);

  useEffect(() => {
    if (hasSort || hasUserSort) setShowSortDropdown(true);
    else setShowSortDropdown(false);
  }, [hasSort, hasUserSort]);

  return (
    <Grid
      container
      alignItems={'center'}
      justifyContent='space-between'
      height='45px'
    >
      <Grid container item alignItems='center' xs={6}>
        <Tooltip
          title={isDisabled ? 'Cannot sort Date field' : sortOrderToolTip}
          placement='top'
          sx={{ pointerEvents: 'auto' }}
        >
          <Switch
            checked={!isDisabled && (hasSort || hasUserSort)}
            disabled={isDisabled}
            onChange={(e, value) => {
              setShowSortDropdown(value);
              if (!value) setSortOrder('');
              if (value) setSortOrder('descending');

              setSortOrderToolTip(
                value === false
                  ? 'Click to select sort order'
                  : ' Click to disable the selected sort order'
              );
              setHasUserSort(value);
            }}
          />
        </Tooltip>
        <MDTypography
          variant='sm'
          color='info'
          opacity={isDisabled ? 0.5 : 1}
          ml={1}
        >
          Sort Order
        </MDTypography>
      </Grid>
      {(hasSort || showSortDropdown) && !isDisabled && (
        <Grid item>
          <Select
            sx={{ p: '10px', width: '145px' }}
            color='secondary'
            MenuProps={{
              disablePortal: true,
              sx: { top: '16px', left: '12px' },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'right'
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'right'
              }
            }}
            value={sortOrder || ''}
            onChange={(e) => {
              setSortOrder(e.target.value);
              sendAmplitudeData(SELECT_SORT_ORDER, {
                'Sort Type': e.target.value
              });
            }}
            displayEmpty
            renderValue={(selected) => {
              if (selected === '')
                return (
                  <MDBox
                    display='flex'
                    alignItems='center'
                    justifyContent='space-between'
                  >
                    <MDTypography variant='sm' color='grey'>
                      Select here
                    </MDTypography>
                  </MDBox>
                );

              return (
                <MDBox
                  display='flex'
                  alignItems='center'
                  justifyContent='space-between'
                >
                  <MDTypography
                    variant='sm'
                    color='info'
                    sx={{ textTransform: 'capitalize !important' }}
                  >
                    {selected}
                  </MDTypography>
                </MDBox>
              );
            }}
          >
            <MenuItem value={'descending'}>
              <MDTypography variant='sm' color='info' textGradient>
                Descending
              </MDTypography>
            </MenuItem>
            <MenuItem value={'ascending'}>
              <MDTypography variant='sm' color='info' textGradient>
                Ascending
              </MDTypography>
            </MenuItem>
          </Select>
        </Grid>
      )}
    </Grid>
  );
};

export default SortOrder;
